import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { ApexSign, AxialRotationEnum, Consts } from '@ortho-next/nextray-core';
import { AppModel } from '../../../../nextray/Models/AppModel';
import { AnatomicalSideLabelPipe } from '../../../shared';
import { VwBaseComponent } from '../../shared';


/**
 * Deformity tables of mechanical analysis.
 */
@Component({
	selector: 'vw-def-mechanical',
	templateUrl: './vw-def-mechanical.component.html',
	imports: [
		AnatomicalSideLabelPipe
	],
	changeDetection: ChangeDetectionStrategy.Eager,
})
export class VwDefMechanicalComponent extends VwBaseComponent {

	model = input.required<AppModel>();

	apAngles = { ...Consts.paleyAngles.AP };
	ltAngles = { ...Consts.paleyAngles.LT };

	apMeasures = computed(() => this.model().isAPMechanicalAxisInserted ? this.model().measures : undefined);
	ctrlMeasures = computed(() => this.model().isContralateralAxisInserted ? this.model().measures : undefined);
	ltMeasures = computed(() => this.model().isLTMechanicalAxisInserted ? this.model().measures : undefined);

	// LLD table

	longlegLLD = computed(() => this.calculateLLD(this.apMeasures()?.def_ap_fullLength, this.ctrlMeasures()?.def_ap_fullLength_ctrlateral));
	femurLLD = computed(() => this.calculateLLD(this.apMeasures()?.def_ap_femurLength, this.ctrlMeasures()?.def_ap_femurLength_ctrlateral));
	tibiaLLD = computed(() => this.calculateLLD(this.apMeasures()?.def_ap_tibiaLength, this.ctrlMeasures()?.def_ap_tibiaLength_ctrlateral));
	trueLength = computed(() => {
		return this.apMeasures()?.def_ap_femurLength && this.apMeasures()?.def_ap_tibiaLength ? this.apMeasures()?.def_ap_femurLength! + this.apMeasures()?.def_ap_tibiaLength! : undefined;
	});
	trueLengthCL = computed(() => {
		return this.ctrlMeasures()?.def_ap_femurLength_ctrlateral && this.ctrlMeasures()?.def_ap_tibiaLength_ctrlateral ? this.ctrlMeasures()?.def_ap_femurLength_ctrlateral! + this.ctrlMeasures()?.def_ap_tibiaLength_ctrlateral! : undefined;
	});
	trueLengthLLD = computed(() => this.calculateLLD(this.trueLength(), this.trueLengthCL()));

	private calculateLLD(length?: number, cl_length?: number): string {
		if (length && cl_length) {
			const lld = length - cl_length;
			let sign = null;
			if (lld > 0) sign = this.labels().VIEW_DEFORMITY_COMPONENT_LONG;
			if (lld < 0) sign = this.labels().VIEW_DEFORMITY_COMPONENT_SHORT;
			return sign ? `${Math.abs(lld)} ${sign}` : `${lld}`;
		}
		return '';
	}

	// Apex table

	apFemurMeasure = computed(() => {
		return this.apMeasures()?.def_ap_apexFemur || this.apMeasures()?.def_ap_apexFemur === 0 ? `${this.apMeasures()?.def_ap_apexFemur} ${this.apexSignLabel(this.apMeasures()?.def_ap_apexFemurSign)}` : '';
	});
	apTibiaMeasure = computed(() => {
		return this.apMeasures()?.def_ap_apexTibia || this.apMeasures()?.def_ap_apexTibia === 0 ? `${this.apMeasures()?.def_ap_apexTibia} ${this.apexSignLabel(this.apMeasures()?.def_ap_apexTibiaSign)}` : '';
	});
	ltTibiaMeasure = computed(() => {
		return this.ltMeasures()?.def_lt_apexTibia || this.ltMeasures()?.def_lt_apexTibia === 0 ? `${this.ltMeasures()?.def_lt_apexTibia} ${this.apexSignLabel(this.ltMeasures()?.def_lt_apexTibiaSign)}` : '';
	});
	axRotFemurMeasure = computed(() => {
		const femurAxialRot = this.model().def_femur_axialRotation;
		const femurAxialRotSign = this.model().def_femur_axialRotationSign;
		return femurAxialRot || femurAxialRot == 0 ? `${femurAxialRot} ${this.axialRotSignLabel(femurAxialRotSign)}` : '';
	});
	axRotTibiaMeasure = computed(() => {
		const tibiaAxialRot = this.model().def_tibia_axialRotation;
		const tibiaAxialRotSign = this.model().def_tibia_axialRotationSign;
		return tibiaAxialRot || tibiaAxialRot === 0 ? `${tibiaAxialRot} ${this.axialRotSignLabel(tibiaAxialRotSign)}` : '';
	});

	/**
	 * Map label for apex sign.
	 */
	apexSignLabel(sign?: string): string | undefined {
		switch (sign) {
			case ApexSign[ApexSign.varus]: return this.labels().APEX_SIGN_VARUS;
			case ApexSign[ApexSign.valgus]: return this.labels().APEX_SIGN_VALGUS;
			default: return sign;
		}
	}

	/**
	 * Map label for axial rotation sign.
	 */
	axialRotSignLabel(sign?: AxialRotationEnum): string {
		switch (sign) {
			case AxialRotationEnum.external: return this.labels().AXIAL_ROT_SIGN_EXTERNAL;
			case AxialRotationEnum.internal: return this.labels().AXIAL_ROT_SIGN_INTERNAL;
			default: return '';
		}
	}

}
